import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { WizardProgress } from '../components/verification/wizard/WizardProgress';
import { TermsStep } from '../components/verification/wizard/TermsStep';
import { InfoStep } from '../components/verification/wizard/InfoStep';
import { ContractStep } from '../components/verification/wizard/ContractStep';
import { BankStep } from '../components/verification/wizard/BankStep';
import { UserInfo, BankInfo } from '../types/verification';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useLanguage } from '../contexts/LanguageContext';

const STORAGE_KEY = 'verification_wizard_state';

export const VerificationWizardPage = () => {
  const navigate = useNavigate();
  const { translations } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState<'terms' | 'info' | 'contract' | 'bank'>('terms');
  const [userType, setUserType] = useState<'guide' | 'organizer'>('guide');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [contractUrl, setContractUrl] = useState<string | undefined>();
  const [signature, setSignature] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo>({
    fullName: '',
    passportNumber: '',
    passportIssuanceDate: '',
    nationality: '',
    country: '',
    mobileNumber: '',
    email: '',
    agent: '',
  });
  const [bankInfo, setBankInfo] = useState<BankInfo>({
    bankName: '',
    accountNumber: '',
    swiftCode: '',
    bankBranch: '',
    ifscCode: '',
  });

  // Check for existing verification
  useEffect(() => {
    const checkExistingVerification = async () => {
      try {
        const userId = localStorage.getItem('userId');
        if (!userId) {
          throw new Error('User not found');
        }

        setLoading(true);

        const verificationsRef = collection(firestore, 'verifications');
        const q = query(verificationsRef, where('userId', '==', userId));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const verificationDoc = querySnapshot.docs[0];

          setTimeout(() => {
            navigate(`/verification-details/${verificationDoc.id}`);
          }, 1000);
          return;
        }

        const userRole = localStorage.getItem('userRole');
        if (userRole === 'organizer') {
          setUserType('organizer');
        } else if (userRole === 'guide') {
          setUserType('guide');
        }

        const savedState = localStorage.getItem(STORAGE_KEY);
        if (savedState) {
          const parsedState = JSON.parse(savedState);
          setCurrentStep(parsedState.currentStep || 'terms');
          setTermsAccepted(parsedState.termsAccepted || false);
          setUserInfo(parsedState.userInfo || userInfo);
          setBankInfo(parsedState.bankInfo || bankInfo);
          setContractUrl(parsedState.contractUrl);
          setSignature(parsedState.signature || false);
        }

        setLoading(false);
      } catch (error: any) {
        console.error('Error checking verification:', error);
        toast.error(error.message || translations?.verificationWizardPage?.errors?.checkVerification || 'Failed to check verification status');
        setLoading(false);
      }
    };

    checkExistingVerification();
  }, [navigate, translations]);

  useEffect(() => {
    const stateToSave = {
      currentStep,
      termsAccepted,
      userInfo,
      bankInfo,
      contractUrl,
      signature,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  }, [currentStep, termsAccepted, userInfo, bankInfo, contractUrl, signature]);

  const handleSubmit = async (contractUrl?: string) => {
    if (submitting) return; // Prevent double submission
    
    try {
      setSubmitting(true);
      const userId = localStorage.getItem('userId');
      const userName = localStorage.getItem('userName');

      if (!userId || !userName) {
        throw new Error(translations?.verificationWizardPage?.errors?.userNotFound || 'User information not found');
      }

      const shouldAutoApprove =
        userType === 'organizer' &&
        Boolean(contractUrl) &&
        signature &&
        termsAccepted;

      const verificationData = {
        userType,
        termsAccepted,
        userInfo,
        bankInfo: userType === 'organizer' ? bankInfo : null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        userId,
        userName,
        contractUrl,
        signature,
        status: (shouldAutoApprove ? 'approved' : 'pending') as const,
        reviewedBy: shouldAutoApprove ? 'system' : null,
        reviewedAt: shouldAutoApprove ? Date.now() : null,
        rejectionReason: null
      };

      const docRef = await addDoc(
        collection(firestore, 'verifications'),
        verificationData
      );

      localStorage.removeItem(STORAGE_KEY);
      toast.success(translations?.verificationWizardPage?.success?.verificationSubmitted || 'Verification submitted successfully');
      navigate(`/verification-details/${docRef.id}`);
    } catch (error: any) {
      console.error('Error submitting verification:', error);
      toast.error(error.message || translations?.verificationWizardPage?.errors?.verificationFailed || 'Failed to complete verification');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 'terms':
        return (
          <TermsStep
            termsAccepted={termsAccepted}
            setTermsAccepted={setTermsAccepted}
            onNext={() => setCurrentStep('info')}
          />
        );
      case 'info':
        return (
          <InfoStep
            userInfo={userInfo}
            setUserInfo={setUserInfo}
            onNext={() => userType === 'organizer' ? setCurrentStep('bank') : setCurrentStep('contract')}
            onBack={() => setCurrentStep('terms')}
          />
        );
      case 'contract':
        return (
          <ContractStep
            onNext={(url?: string) => {
              setContractUrl(url);
              handleSubmit(url);
            }}
            onBack={() => userType === 'organizer' ? setCurrentStep('bank') : setCurrentStep('info')}
            userType={userType}
            signature={signature}
            onSignatureChange={setSignature}
          />
        );
      case 'bank':
        return (
          <BankStep
            bankInfo={bankInfo}
            setBankInfo={setBankInfo}
            onSubmit={() => setCurrentStep('contract')}
            onBack={() => setCurrentStep('info')}
            contractUrl={contractUrl}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4">
        <WizardProgress currentStep={currentStep} userType={userType} />
        <div className="bg-white p-8 rounded-xl shadow-sm">{renderStep()}</div>
      </div>
    </div>
  );
};
